<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { provide, computed } from 'vue'
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";

// 1) Expect 'id' from the parent.
const props = defineProps<{ id: string }>();
const emit = defineEmits(["content-update"]);

// 2) Access the definition data (read-only) via the content manager.
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// Optional report injection for templating defaultValue
const report = inject<any | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const logger = useLogger("Accordion");
const { applyTemplate } = useTemplating(templateContext, { logger });
const templatedDefaultValue = computed(() => applyTemplate(definitionData.value?.defaultValue || ""));

const highlightActive = computed(() => definitionData.value?.highlightActive ?? false)
provide('accordion-highlight-active', highlightActive)


// 4) Define commands for the context menu.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Accordion editors",
    commands: [
      {
        id: "edit",
        name: "Edit Accordion",
        description: "Edit accordion settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: { ...definitionData.value },
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Accordion",
    icon: "lucide:panel-top-open",
  },
  filter: ["Accordion Item"],
});
</script>

<template>
  <Accordion
    :id="props.id"
    :type="definitionData.type || 'single'"
    :value="templatedDefaultValue"
    collapsible
    :class="cn($attrs.class ?? '')"
  >
    <slot />
  </Accordion>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.ACCORDION,
  editor: () => import("#bb/components/editor/container/accordion.vue"),
  icon: "lucide:panel-top-open",
  type: "Accordion",
  description: "A container block for grouping content in an accordion format.",
  default: {
    type: "single",
    defaultValue: "",
    highlightActive: false,
    children: [],
  },
  categories: ["Layout"],
  filter: ["Accordion Item"],
  order: 12002,
};
</script>
